import React, { useEffect, useMemo, useState } from "react";
import LabShell from "./LabShell";
import { Panel, EmptyState, formatRelative } from "./labUi";
import { fetchRuns, fetchRunReport, isBackendUnavailable } from "../../services/LabService";
import { labHref, navigate } from "./labRouter";

/**
 * The backend doesn't expose /history; we derive it from /runs?status=SUCCEEDED
 * plus per-run /report calls in parallel. Each report's headline metric is the
 * lowest min_ade_mean across its experiments (best cell).
 */

const bestMinAde = (report) => {
    const values = (report?.experiments || [])
        .map((e) => e.metrics?.min_ade_mean)
        .filter((v) => typeof v === "number");
    if (!values.length) return null;
    return Math.min(...values);
};

const HistoryChart = ({ records }) => {
    if (!records || records.length === 0) return null;
    const width = 720;
    const height = 220;
    const padX = 40;
    const padY = 24;

    const points = records
        .filter((r) => typeof r.headline_metric === "number")
        .map((r) => ({ ...r, t: new Date(r.completed_at).getTime() }))
        .sort((a, b) => a.t - b.t);
    if (points.length === 0) return null;

    const xs = points.map((p) => p.t);
    const ys = points.map((p) => p.headline_metric);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys) * 0.98;
    const maxY = Math.max(...ys) * 1.02;
    const xScale = (x) => padX + ((x - minX) / Math.max(1, maxX - minX)) * (width - 2 * padX);
    const yScale = (y) => padY + (1 - (y - minY) / Math.max(0.0001, maxY - minY)) * (height - 2 * padY);

    const plotted = points.map((p) => ({ x: xScale(p.t), y: yScale(p.headline_metric), rec: p }));
    const path = plotted.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");

    return (
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
            <path d={path} fill="none" stroke="#6366f1" strokeWidth={2} />
            {plotted.map((p) => (
                <circle
                    key={p.rec.run_id}
                    cx={p.x}
                    cy={p.y}
                    r={4}
                    fill="#fff"
                    stroke="#6366f1"
                    strokeWidth={2}
                >
                    <title>{`${p.rec.proposal_id || p.rec.run_id}\nmin_ADE_mean: ${p.rec.headline_metric.toFixed(3)}\n${p.rec.completed_at}`}</title>
                </circle>
            ))}
            <text x={4} y={padY + 6} className="fill-slate-400 text-[10px]">{maxY.toFixed(2)}</text>
            <text x={4} y={height - padY} className="fill-slate-400 text-[10px]">{minY.toFixed(2)}</text>
        </svg>
    );
};

const LabHistory = () => {
    const [state, setState] = useState({ loading: true, records: [], error: null });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const runs = await fetchRuns({ status: "SUCCEEDED", limit: 30 });
                const items = runs?.items || [];

                // Fetch each report in parallel. Best-effort: missing reports
                // just lack their headline_metric.
                const enriched = await Promise.all(items.map(async (run) => {
                    let headline_metric = null;
                    let first_experiment_decoding = null;
                    try {
                        const report = await fetchRunReport(run.run_id);
                        headline_metric = bestMinAde(report);
                        first_experiment_decoding = report?.experiments?.[0]?.decoding || null;
                    } catch (_) { /* tolerate */ }
                    return {
                        run_id: run.run_id,
                        proposal_id: run.proposal_id,
                        completed_at: run.stopped_at,
                        headline_metric,
                        first_experiment_decoding,
                    };
                }));

                if (!cancelled) setState({ loading: false, records: enriched, error: null });
            } catch (error) {
                if (isBackendUnavailable(error)) {
                    if (!cancelled) setState({ loading: false, records: [], error: null });
                } else if (!cancelled) {
                    setState({ loading: false, records: [], error });
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const sorted = useMemo(
        () => [...state.records].sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at)),
        [state.records]
    );

    return (
        <LabShell activeKey="history">
            <Panel title="Run history">
                {state.loading ? (
                    <div className="text-sm text-slate-500">Loading history…</div>
                ) : state.error ? (
                    <div className="text-sm text-red-600">{state.error.message}</div>
                ) : sorted.length === 0 ? (
                    <EmptyState title="No history yet" body="Once runs complete, their headline metrics will plot here." />
                ) : (
                    <>
                        <HistoryChart records={sorted} />
                        <div className="mt-6 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs text-slate-500 uppercase tracking-wide">
                                    <tr className="border-b border-slate-200 dark:border-slate-800">
                                        <th className="text-left px-2 py-2 font-medium">Run</th>
                                        <th className="text-left px-2 py-2 font-medium">Proposal</th>
                                        <th className="text-left px-2 py-2 font-medium">Completed</th>
                                        <th className="text-right px-2 py-2 font-medium">best min_ADE</th>
                                        <th className="text-left px-2 py-2 font-medium">Decoding</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...sorted].reverse().map((r) => (
                                        <tr
                                            key={r.run_id}
                                            className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer"
                                            onClick={() => navigate(labHref(`experiments/${r.run_id}`))}
                                        >
                                            <td className="px-2 py-2 font-mono text-xs">{(r.run_id || "").slice(-14)}</td>
                                            <td className="px-2 py-2">{r.proposal_id || "—"}</td>
                                            <td className="px-2 py-2 text-slate-600 dark:text-slate-300">{formatRelative(r.completed_at)}</td>
                                            <td className="px-2 py-2 text-right font-mono">
                                                {typeof r.headline_metric === "number" ? r.headline_metric.toFixed(3) : "—"}
                                            </td>
                                            <td className="px-2 py-2 text-xs text-slate-500 font-mono">
                                                {r.first_experiment_decoding
                                                    ? Object.entries(r.first_experiment_decoding).map(([k, v]) => `${k}=${v}`).join(" ")
                                                    : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </Panel>
        </LabShell>
    );
};

export default LabHistory;
